/**
 * The metadata half of a document, as the backend stores it.
 *
 * Markers, walk area, viewer settings, start view, camera, timeline, pose sets
 * and per-splat entity transforms are what an author tweaks constantly and are
 * tiny next to the splat source, so they ship as a standalone sidecar
 * (`editor-meta.json`) that a metadata-only save rewrites without re-uploading
 * the `.ssproj`. On reopen the sidecar is overlaid on whatever the `.ssproj`
 * carried, so the freshest edit wins.
 *
 * CANONICAL OWNERSHIP: the live scene is the single source of truth. This is
 * the EDITOR projection; the PUBLIC projection is scene-doc.ts (scene.json for
 * the viewer). A new field belongs in both.
 *
 * `doc.serializeBytes` produces the full `.ssproj` in memory through the
 * upstream saver (see `doc.saveToStream` in doc.ts), so the backend upload
 * writes exactly what File ▸ Save would.
 */

import { Quat, Vec3 } from 'playcanvas';

import type { Events } from '../events';
import type { Scene } from '../scene';
import type { Splat } from '../splat';
import { MemoryWritableStream } from './memory-stream';
import { markerToJson } from './scene-doc';

/** invoke() that tolerates a function nobody registered (an optional module). */
const invokeIf = (events: Events, name: string, ...args: unknown[]): unknown => {
    return events.functions.has(name) ? events.invoke(name, ...args) : undefined;
};

const registerDocMeta = (events: Events, scene: Scene) => {
    const buildDocMeta = () => ({
        version: 0,
        camera: scene.camera.docSerialize(),
        view: events.invoke('docSerialize.view'),
        poseSets: events.invoke('docSerialize.poseSets'),
        timeline: events.invoke('docSerialize.timeline'),
        tracks: invokeIf(events, 'tracks.serialize') ?? [],
        lighting: invokeIf(events, 'lighting.get') ?? null,
        startPose: invokeIf(events, 'camera.getStartPose') ?? null,
        // markers normalised to plain [x,y,z] so they survive JSON round-trips
        markers: ((invokeIf(events, 'markers.serialize') as unknown[] | null) ?? []).map(markerToJson),
        // snapshot() (not serialize()) so partial walk work survives a reload
        walk: invokeIf(events, 'walk.snapshot') ?? null,
        viewer: invokeIf(events, 'viewer.settings') ?? null,
        // per-splat entity transform, keyed by name so a reorder can't
        // cross-apply; applied through the absolute move() so the freshest
        // source simply wins
        splatXforms: (events.invoke('scene.allSplats') as Splat[]).map((s) => {
            const p = s.entity.getLocalPosition();
            const r = s.entity.getLocalRotation();
            const sc = s.entity.getLocalScale();
            return {
                name: s.name,
                position: [p.x, p.y, p.z],
                rotation: [r.x, r.y, r.z, r.w],
                scale: [sc.x, sc.y, sc.z]
            };
        })
    });

    // Every field optional/tolerant so older sidecars and partial ones work.
    const applyDocMeta = (document: any) => {
        if (!document || typeof document !== 'object') return;
        if (document.timeline) events.invoke('docDeserialize.timeline', document.timeline);
        if (document.poseSets) events.invoke('docDeserialize.poseSets', document.poseSets, document.camera?.fov);
        if (document.view) events.invoke('docDeserialize.view', document.view);
        if (document.camera) scene.camera.docDeserialize(document.camera);
        if (document.lighting && typeof document.lighting === 'object') events.fire('lighting.load', document.lighting);
        if (Array.isArray(document.tracks) && events.functions.has('tracks.deserialize')) {
            events.invoke('tracks.deserialize', document.tracks);
        }
        if (document.startPose && Array.isArray(document.startPose.position)) {
            events.fire('camera.setStartPoseData', document.startPose);
        }
        if (document.markers) events.fire('markers.deserialize', document.markers);
        if (document.walk) events.fire('walk.deserialize', document.walk);
        if (document.viewer) events.fire('viewer.deserialize', document.viewer);
        if (Array.isArray(document.splatXforms)) {
            const splats = events.invoke('scene.allSplats') as Splat[];
            for (const x of document.splatXforms) {
                if (!x || !x.position || !x.rotation || !x.scale) continue;
                const s = splats.find(sp => sp.name === x.name);
                if (s) s.move(new Vec3(x.position), new Quat(x.rotation), new Vec3(x.scale));
            }
        }
        scene.forceRender = true;
    };

    events.function('doc.serializeMeta', (): Uint8Array => {
        return new TextEncoder().encode(JSON.stringify(buildDocMeta()));
    });

    events.function('doc.applyMeta', (meta: unknown) => applyDocMeta(meta));

    // The whole .ssproj, in memory, exactly as File ▸ Save would write it.
    events.function('doc.serializeBytes', async (): Promise<Uint8Array> => {
        const stream = new MemoryWritableStream();
        const ok = await events.invoke('doc.saveToStream', stream, 'scene.ssproj');
        if (!ok) throw new Error('document serialization failed');
        return stream.bytes;
    });
};

export { registerDocMeta };
